import { Dropdown, Label, Stack, IDropdownOption } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import { useTranslation } from 'react-i18next';
import { DateRangeEnd, DateSelectorType } from '../enums';
import { DateRangeSelectorChoice } from './DateRangeSelectorChoice';
import { IDateSelectorTypeState, IDateState } from '../types';
import { dropdownStyles } from '../dropdownStyles';
import { useThemeData } from '@msrceng/theme';
import { format } from 'date-fns';
import { useDatePickerOptions } from './useDatePickerOptions';
import { useAnalytics } from '@msrceng/analytics';
import { useEffect } from 'react';

/**
 * A UI that lets you pick a date more easily by patch tuesday,
 * by month/year, or by a specific date
 */
export const DateRangeSelector = ({
  dateRangeEnd,
  label,
  date,
  setDate,
  type,
  setType,
}: {
  dateRangeEnd: DateRangeEnd;
  label: string;
  /** TODO: this isn't yet implemented. */
  isValid?: boolean;
} & IDateSelectorTypeState &
  IDateState) => {
  const { isDarkMode, theme } = useThemeData();
  const { appInsights } = useAnalytics();

  const [t] = useTranslation(['search', 'translation']);
  const id = useId(`date-picker-${dateRangeEnd}`);

  const datePickerOptions = useDatePickerOptions(dateRangeEnd);
  const dateLabel = date ? format(date, 'PP') : '—';

  // Track how users prefer to select dates
  useEffect(() => {
    if (appInsights?.trackEvent) {
      appInsights.trackEvent({
        name: 'Date range selection',
        properties: { dateRangeEnd, date, type },
      });
    }
  }, [dateRangeEnd, appInsights, date, type]);

  return (
    <div style={{ padding: '0.25em 0' }} role='group' aria-label={label}>
      <Label htmlFor={id}>
        {label}: <output>{dateLabel}</output>
      </Label>
      <Stack horizontal horizontalAlign={'stretch'} wrap>
        <Dropdown
          label={t('datePickerMode', { defaultValue: 'Mode', ns: 'search' })}
          selectedKey={type}
          onChange={(_e, k) => {
            if (!k || !k.key) {
              setType(undefined);
              return;
            }
            setType(DateSelectorType[k.key]);
          }}
          options={datePickerOptions}
          dropdownWidth={'auto'}
          styles={{
            root: { width: '11em' },
            ...dropdownStyles(isDarkMode, theme),
          }}
        />
        <Stack horizontal horizontalAlign={'stretch'} wrap>
          <DateRangeSelectorChoice {...{ type, dateRangeEnd, date, setDate }} />
        </Stack>
      </Stack>
    </div>
  );
};

export interface IDateRangeSelectorDropdownOption extends IDropdownOption {
  key: DateSelectorType;
}
